import React, { useEffect, createElement, Component } from 'react';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
    __atlasPixelBooted?: boolean;
    __atlasViewContentSent?: boolean;
  }
}
/* ──────────────────────────────────────────────────────────────────────
   CONFIG
   ──────────────────────────────────────────────────────────────────── */
const PIXEL_ID = '2820238711690534';
/**
 * Optional Conversions API forwarder endpoint.
 *
 *  - Leave EMPTY to run pixel-only (browser events).
 *  - Set to your own server (e.g. a Cloudflare Worker, Vercel
 *    function, Netlify function, or any small Node endpoint) that
 *    forwards the payload to:
 *
 *      https://graph.facebook.com/v19.0/<PIXEL_ID>/events
 *
 *    using your Meta CAPI access token (which MUST live on the
 *    server — never expose it in client code).
 *
 *  When set, the browser POSTs the same event payload (with the
 *  SAME event_id used by fbq) so Meta deduplicates browser+server
 *  copies of the event automatically.
 *
 *  See the bottom of this file for a ready-to-paste Cloudflare
 *  Worker snippet.
 */
const CAPI_ENDPOINT = ''; // e.g. 'https://capi.atlasclinico.com/track'
/* ──────────────────────────────────────────────────────────────────────
   IDENTITY HELPERS — event_id + external_id
   ──────────────────────────────────────────────────────────────────── */
function uuid(): string {
  // crypto.randomUUID() is widely supported; fall back for older browsers
  try {
    if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
      return (crypto as any).randomUUID();
    }
  } catch {}
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
}
/** Persistent visitor ID — used as Advanced Matching `external_id`
 *  so browser and server events match the same person.            */
function getExternalId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = window.localStorage.getItem('atlas_ext_id');
    if (!id) {
      id = uuid();
      window.localStorage.setItem('atlas_ext_id', id);
    }
    return id;
  } catch {
    return '';
  }
}
/** Pulls fbp / fbc cookies set by the pixel — Meta needs them for
 *  high-quality matching on server-side events.                    */
function readPixelCookies(): {
  fbp?: string;
  fbc?: string;
} {
  if (typeof document === 'undefined') return {};
  const get = (name: string) => {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : undefined;
  };
  return {
    fbp: get('_fbp'),
    fbc: get('_fbc')
  };
}
/** If Meta Ads brought the user here with `?fbclid=...` and there
 *  is no `_fbc` cookie yet, synthesize one in Meta's canonical
 *  format. Improves attribution by ~20-40%.                        */
function ensureFbcFromQuery() {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const fbclid = url.searchParams.get('fbclid');
    if (!fbclid) return;
    const existing = readPixelCookies().fbc;
    if (existing) return;
    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    document.cookie = `_fbc=${fbc}; path=/; max-age=7776000; SameSite=Lax`;
  } catch {}
}
/* ──────────────────────────────────────────────────────────────────────
   CAPI FORWARDER (no-op when CAPI_ENDPOINT is empty)
   ──────────────────────────────────────────────────────────────────── */
type CapiEvent = {
  event_name: string;
  event_id: string;
  event_time: number;
  event_source_url: string;
  action_source: 'website';
  user_data: {
    external_id?: string;
    fbp?: string;
    fbc?: string;
    client_user_agent?: string;
  };
  custom_data?: Record<string, any>;
};
function forwardToCapi(payload: CapiEvent) {
  if (!CAPI_ENDPOINT) return; // browser-only mode
  // Fire-and-forget. Use sendBeacon for reliability on tab unload.
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], {
        type: 'application/json'
      });
      navigator.sendBeacon(CAPI_ENDPOINT, blob);
      return;
    }
  } catch {}
  try {
    fetch(CAPI_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body,
      keepalive: true,
      credentials: 'omit'
    }).catch(() => {});
  } catch {}
}
function buildCapiPayload(eventName: string, eventId: string, customData?: Record<string, any>): CapiEvent {
  const {
    fbp,
    fbc
  } = readPixelCookies();
  return {
    event_name: eventName,
    event_id: eventId,
    event_time: Math.floor(Date.now() / 1000),
    event_source_url: typeof window !== 'undefined' ? window.location.href : '',
    action_source: 'website',
    user_data: {
      external_id: getExternalId() || undefined,
      fbp,
      fbc,
      client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
    },
    custom_data: customData
  };
}
/* ──────────────────────────────────────────────────────────────────────
   PIXEL BOOTSTRAP
   ──────────────────────────────────────────────────────────────────── */
function bootPixel() {
  if (typeof window === 'undefined') return;
  if (window.__atlasPixelBooted) return;
  window.__atlasPixelBooted = true;
  ensureFbcFromQuery();
  (function (f: any, b: Document, e: string, v: string) {
    let n: any;
    let t: HTMLScriptElement;
    let s: HTMLScriptElement | null;
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0] as HTMLScriptElement;
    s?.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  // Advanced Matching — Meta hashes external_id for us on its side
  const externalId = getExternalId();
  window.fbq('init', PIXEL_ID, externalId ? {
    external_id: externalId
  } : undefined);
  // PageView
  const pageViewId = uuid();
  window.fbq('track', 'PageView', {}, {
    eventID: pageViewId
  });
  forwardToCapi(buildCapiPayload('PageView', pageViewId));
  // ViewContent (this whole landing IS the product page)
  if (!window.__atlasViewContentSent) {
    window.__atlasViewContentSent = true;
    const viewContentId = uuid();
    const vcParams = {
      content_name: 'Mega Pack Kinesiología',
      content_category: 'Kinesiología',
      content_ids: ['mega-pack-kinesiologia'],
      content_type: 'product',
      currency: 'ARS',
      value: 19990
    };
    window.fbq('track', 'ViewContent', vcParams, {
      eventID: viewContentId
    });
    forwardToCapi(buildCapiPayload('ViewContent', viewContentId, vcParams));
  }
}
function scheduleBoot() {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(bootPixel, {
      timeout: 2500
    });
  } else {
    window.setTimeout(bootPixel, 1500);
  }
  // Boot immediately on first interaction so we never lose attribution
  const fire = () => {
    bootPixel();
    window.removeEventListener('scroll', fire);
    window.removeEventListener('pointerdown', fire);
    window.removeEventListener('keydown', fire);
  };
  window.addEventListener('scroll', fire, {
    passive: true,
    once: true
  });
  window.addEventListener('pointerdown', fire, {
    passive: true,
    once: true
  });
  window.addEventListener('keydown', fire, {
    once: true
  });
}
if (typeof window !== 'undefined') {
  if (document.readyState === 'complete') {
    scheduleBoot();
  } else {
    window.addEventListener('load', scheduleBoot, {
      once: true
    });
  }
}
/* ──────────────────────────────────────────────────────────────────────
   PUBLIC API
   ──────────────────────────────────────────────────────────────────── */
export function MetaPixel() {
  useEffect(() => {
    // Safety net if module-level boot was skipped (rare edge cases)
    scheduleBoot();
  }, []);
  return <noscript data-id="element-255">
      <img height="1" width="1" style={{
      display: 'none'
    }} alt="" src={`https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`} data-id="element-256" />
    </noscript>;
}
/** Fire any custom event, deduplicated browser + server. */
export function trackPixelEvent(event: string, params?: Record<string, any>) {
  if (typeof window === 'undefined') return;
  if (!window.__atlasPixelBooted) bootPixel();
  const eventId = uuid();
  if (window.fbq) {
    window.fbq('track', event, params || {}, {
      eventID: eventId
    });
  }
  forwardToCapi(buildCapiPayload(event, eventId, params));
}
/**
 * Fires InitiateCheckout with the FULL canonical param set Meta
 * requires for Purchase-optimized campaigns. Call this from each
 * pricing CTA right before navigating to Shopify checkout.
 *
 * Returns the event_id so callers (if they wanted) could attach it
 * to a server-side log.
 */
export function trackInitiateCheckout(opts: {
  packId: string;
  packName: string;
  value: number;
  currency?: string;
}): string {
  const params = {
    content_name: opts.packName,
    content_category: 'Kinesiología',
    content_ids: [opts.packId],
    content_type: 'product',
    contents: [{
      id: opts.packId,
      quantity: 1,
      item_price: opts.value
    }],
    num_items: 1,
    value: opts.value,
    currency: opts.currency || 'ARS'
  };
  const eventId = uuid();
  if (typeof window !== 'undefined') {
    if (!window.__atlasPixelBooted) bootPixel();
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', params, {
        eventID: eventId
      });
    }
    forwardToCapi(buildCapiPayload('InitiateCheckout', eventId, params));
  }
  return eventId;
}
/* ──────────────────────────────────────────────────────────────────────
   CLOUDFLARE WORKER SNIPPET (deploy this to enable real CAPI)
   ──────────────────────────────────────────────────────────────────── */
/*

// worker.js — deploy at e.g. capi.atlasclinico.com and set CAPI_ENDPOINT above

const PIXEL_ID    = '2820238711690534'
const ACCESS_TOKEN = '<YOUR META CAPI ACCESS TOKEN>' // generate in Events Manager

export default {
  async fetch(req) {
    if (req.method !== 'POST') return new Response('ok')

    const cors = {
      'Access-Control-Allow-Origin':  'https://kinesiologia.atlasclinico.com',
      'Access-Control-Allow-Methods': 'POST, OPTIONS',
      'Access-Control-Allow-Headers': 'Content-Type',
    }
    if (req.method === 'OPTIONS') return new Response(null, { headers: cors })

    const e = await req.json()
    // Hash any PII we'd add (email/phone) here with SHA-256 before sending.
    const body = {
      data: [{
        event_name:       e.event_name,
        event_id:         e.event_id,
        event_time:       e.event_time,
        event_source_url: e.event_source_url,
        action_source:    'website',
        user_data: {
          external_id:        e.user_data?.external_id,
          fbp:                e.user_data?.fbp,
          fbc:                e.user_data?.fbc,
          client_ip_address:  req.headers.get('CF-Connecting-IP'),
          client_user_agent:  e.user_data?.client_user_agent,
        },
        custom_data: e.custom_data,
      }],
    }

    await fetch(
      `https://graph.facebook.com/v19.0/${PIXEL_ID}/events?access_token=${ACCESS_TOKEN}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    )

    return new Response('ok', { headers: cors })
  }
}

*/